import { useEffect, useMemo, useRef, useState } from 'react';
import { listTaxonomy, type TaxonomyKind } from '../lib/panelApi';

export type TokenMultiSelectProps = {
  accessToken: string;
  /** Taxonomy dimension to pull suggestions from. */
  kind: TaxonomyKind;
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  /** Allow committing a typed value that is not in the suggestion list. */
  allowCreate?: boolean;
  className?: string;
};

// Sentinel row id for the "create new" option inside the dropdown.
const CREATE_ROW = '\u0000__create__';

/**
 * Advanced multi-select with chips + type-ahead, backed by the panel's
 * taxonomy API. The operator picks existing entries from a dropdown
 * instead of hand-typing comma-separated values. New values can still
 * be created on the fly (the backend upserts taxonomy from book
 * metadata on save), which is required for brand-new authors/tags.
 */
export function TokenMultiSelect({
  accessToken,
  kind,
  label,
  values,
  onChange,
  placeholder,
  allowCreate = true,
  className,
}: TokenMultiSelectProps) {
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await listTaxonomy(accessToken, kind, { limit: 200, sort: 'book_count' });
        if (!cancelled) setOptions(res.items.map((i) => i.name).filter(Boolean));
      } catch {
        // Best-effort: suggestions are a convenience. The operator can
        // still type free values when the list fails to load.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, kind]);

  // Close the dropdown when clicking outside the component.
  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const lowerValues = useMemo(() => new Set(values.map((v) => v.toLowerCase())), [values]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = options.filter((o) => !lowerValues.has(o.toLowerCase()));
    if (!q) return base.slice(0, 50);
    return base.filter((o) => o.toLowerCase().includes(q)).slice(0, 50);
  }, [options, query, lowerValues]);

  const canCreate =
    allowCreate &&
    query.trim() !== '' &&
    !lowerValues.has(query.trim().toLowerCase()) &&
    !options.some((o) => o.toLowerCase() === query.trim().toLowerCase());

  const rows = canCreate ? [...filtered, CREATE_ROW] : filtered;

  function addValue(raw: string) {
    const t = raw.trim();
    setQuery('');
    setHighlight(0);
    if (!t || lowerValues.has(t.toLowerCase())) return;
    onChange([...values, t]);
    inputRef.current?.focus();
  }

  function removeAt(i: number) {
    onChange(values.filter((_, idx) => idx !== i));
  }

  function commitHighlighted() {
    const row = rows[highlight];
    if (row === CREATE_ROW) addValue(query);
    else if (row != null) addValue(row);
    else if (query.trim()) addValue(query);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        commitHighlighted();
        break;
      case ',':
        // Comma is a familiar "commit" key for tag inputs.
        e.preventDefault();
        if (query.trim()) addValue(query);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setOpen(true);
        setHighlight((h) => Math.min(h + 1, Math.max(rows.length - 1, 0)));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
        break;
      case 'Backspace':
        if (query === '' && values.length) {
          e.preventDefault();
          removeAt(values.length - 1);
        }
        break;
      case 'Escape':
        setOpen(false);
        break;
    }
  }

  // Support pasting "a, b, c" (or newline-separated) in one go.
  function onPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text');
    if (!/[,\n]/.test(text)) return;
    e.preventDefault();
    const parts = text
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    const next = [...values];
    const seen = new Set(next.map((v) => v.toLowerCase()));
    for (const p of parts) {
      if (!seen.has(p.toLowerCase())) {
        next.push(p);
        seen.add(p.toLowerCase());
      }
    }
    onChange(next);
    setQuery('');
  }

  return (
    <div className={`field ${className ?? ''}`} ref={rootRef}>
      <div className="label">
        {label}
        {loading ? <span className="muted"> · loading…</span> : null}
      </div>

      <div
        className={`tokenSelect ${open ? 'tokenSelectOpen' : ''}`}
        onClick={() => {
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        {values.map((v, i) => (
          <span className="token" key={`${v}-${i}`}>
            <span className="tokenLabel">{v}</span>
            <button
              type="button"
              className="tokenX"
              aria-label={`Remove ${v}`}
              onClick={(e) => {
                e.stopPropagation();
                removeAt(i);
              }}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          className="tokenInput"
          value={query}
          placeholder={values.length ? '' : placeholder ?? 'Type to search…'}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
      </div>

      {open && rows.length > 0 ? (
        <div className="tokenMenu">
          {filtered.map((o, i) => (
            <button
              type="button"
              key={o}
              className={`tokenOption ${highlight === i ? 'tokenOptionActive' : ''}`}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => addValue(o)}
            >
              {o}
            </button>
          ))}
          {canCreate ? (
            <button
              type="button"
              className={`tokenOption tokenCreate ${highlight === filtered.length ? 'tokenOptionActive' : ''}`}
              onMouseEnter={() => setHighlight(filtered.length)}
              onClick={() => addValue(query)}
            >
              + Add “{query.trim()}”
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
